import { useLocation, useRoute } from "wouter";
import { TabPanel, Tabs, type TabItem } from "../../components/Tabs";
import { AlertsPage } from "../alerts/AlertsPage";
import { HealthPage } from "../health/HealthPage";
import { MyServicesPage } from "../my-services/MyServicesPage";
import "./operate.css";

/**
 * Operate's home (IA slice 7, docs/product/ui-information-architecture.md §3): the App
 * Developer's whole world — services, their health, and the alerts watching them. Mounted
 * at `/operate` plus sibling tab routes — "tab and route are the same thing", same pattern
 * as `GovernPage` (IA slice 5) and `ProjectDetailPage` (IA slice 3).
 *
 * Replaces the prior `SectionSubNav`-based pattern where `/my-services`, `/health`, and
 * `/alerts` were independent top-level routes each mounting their own `OperateSubNav`. Now
 * all three are `TabPanel`s of this single container, with routes `/operate`,
 * `/operate/health`, `/operate/alerts`. Pre-beta clean break — the old paths are retired
 * outright (no alias, no redirect), matching IA slice 6's tombstone policy.
 *
 * No role gating on individual tabs: every fleet role (admin/operator/viewer) sees all
 * three tabs. Per-action mutation gating (alert rule CRUD is admin+operator, viewer
 * view-only) is handled inside `AlertsPage` itself, unchanged (S-8).
 */
export function OperatePage() {
  const [, navigate] = useLocation();
  const [matchHealth] = useRoute("/operate/health");
  const [matchAlerts] = useRoute("/operate/alerts");
  const activeTab = matchHealth ? "health" : matchAlerts ? "alerts" : "services";

  const tabItems: TabItem[] = [
    { id: "services", label: "Services" },
    { id: "health", label: "Health" },
    { id: "alerts", label: "Alerts" },
  ];

  function handleTabChange(id: string) {
    navigate(id === "services" ? "/operate" : `/operate/${id}`);
  }

  return (
    <div className="operate-page">
      <h1 className="visually-hidden">Operate</h1>
      <div className="operate-page__tabs">
        <Tabs items={tabItems} activeId={activeTab} onChange={handleTabChange} ariaLabel="Operate views" />
      </div>

      <TabPanel id="services" activeId={activeTab}>
        <MyServicesPage />
      </TabPanel>
      <TabPanel id="health" activeId={activeTab}>
        <HealthPage />
      </TabPanel>
      <TabPanel id="alerts" activeId={activeTab}>
        <AlertsPage />
      </TabPanel>
    </div>
  );
}
